import React, { useEffect, useState, useContext, useMemo } from 'react';
import union from '@turf/union';
import { range } from 'd3-array';
import { format } from 'd3-format';
import get from 'lodash/get';
import has from 'lodash/has';
import { getTotalAcres } from './geo';
// import Warning from './Warning';
import { FarmContext, GEO_URL } from './FarmContext';
import { AppContext } from './AppContext';
import Histogram from './Histogram';
import { warningIcon } from './Notifications';
import Table from './Table';

const ndviF = format('.2f');

const useDates = () => {
  const year = new Date().getFullYear() - 1;

  const years = useMemo(() => {
    const year = new Date().getFullYear();
    return range(year - 3 , year).reverse();
  }, []);

  const [historical, setHistorical] = React.useState({});
  const [stats, setStats] = React.useState({});
  const [mapLayer, setMapLayer] = React.useState([]);
  const [start, setStart] = useState(`${year}-06-01`);
  const [end, setEnd] = useState(`${year}-08-01`);
  
  const { mapboxRef, showing } = useContext(AppContext);
  const { boundary } = useContext(FarmContext);
  
  useEffect(() => {
    if (boundary.features.length === 0) return;
    if (!showing['NDVI']) return;
    if (getTotalAcres(boundary) > 10000) return;
    const combined = boundary.features.reduce((acc, cur) => {
      return union(acc, cur);
    }); 
    years.forEach(year => {
      fetch(GEO_URL + '/geoserver/ndvi', {
        method: 'POST',
        body: JSON.stringify({
            boundary: combined,
            start: `${year}-01-01`,
            end: `${year}-12-31`,
        }),
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      })
        .then(r => r.json())
        .then(r => {
          setHistorical(s => ({...s, [year]: r}));
        })
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundary])

  // NDVI_autohistogram
  // TODO fix redo/undo
  useEffect(() => {
      if (boundary.features.length === 0) return;
      if (!showing['NDVI']) return;
      if (getTotalAcres(boundary) > 10000) return;
      console.log('fetching ndvi')
      const combined = boundary.features.reduce((acc, cur) => {
        return union(acc, cur);
      });
      fetch(GEO_URL + '/geoserver/ndvi', {
        method: 'POST',
        body: JSON.stringify({
            boundary: combined,
            start: start,
            end: end,
        }),
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      })
        .then(r => r.json())
        .then(r => {
          setMapLayer([{...r, id: 'ndvi'}]);
          setStats(s => ({...s, boundary: r.stats}));
        })

      fetch(GEO_URL + '/geoserver/ndvi_region', {
        method: 'POST',
        body: JSON.stringify({
            boundary: combined,
            start: start,
            end: end,
        }),
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      })
        .then(r => r.json())
        .then(r => {
          setStats(s => ({...s, region: r}));
        })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundary, start, end]);

  useEffect(() => {
    // TODO true?
    const toggle = true;
    updateLayer(mapboxRef, mapLayer, toggle);
  }, [mapLayer, mapboxRef]);

  return { start, setStart, end, setEnd, mapLayer, stats, historical, years };
}


export const DateControl = () => {


  // display min, max, mean , std by year
  // 2yr, 3yr, 5yr
  const { start, setStart, end, setEnd, stats, historical, years } = useDates();
  return <div>
    <div>
      <input id="start" type="date" value={start} onChange={evt => setStart(evt.target.value)}/>
      <span> to </span>
      <input id="end" type="date" value={end} onChange={evt => setEnd(evt.target.value)}/>
    </div>
    <h3>NDVI Histogram</h3>
    <Histogram
      // values={stats.boundary}
      // values2={stats.region}
      values={stats.region}
      avgLine={stats.boundary?.NDVI_median}
    />
    {/*    <div>
      <span style={{width: '20px', height: '20px', padding: '5px', 'backgroundColor': 'darkred', opacity: 0.5}}></span> Region (10 mile radius)
    </div>
    <div>
      <span style={{width: '20px', height: '20px', padding: '5px', 'backgroundColor': 'steelblue', opacity: 0.75}}></span> Boundary
    </div>*/}
    <div>
      <div style={{float: 'left', width: '20px', height: '20px', marginRight: '5px', 'border-right': 'dashed darkred 2px', opacity: 0.5}}></div> Boundary Median
    </div>
    <div>
      <div style={{float: 'left', width: '20px', height: '20px', marginRight: '5px', 'backgroundColor': 'steelblue', opacity: 0.75}}></div> Region (10 mile radius)
    </div>
    <Table headers={['year', 'img', 'stats']} rows={years.filter(y => has(historical, y)).map(y => ({
      year: y,
      img: <img alt="CDL" className="h-24 w-auto max-w-none" src={get(historical, [y, 'source', 'url'])} />,
      stats: <div> 
        <div> Mean: {ndviF(get(historical, [y, 'stats', 'NDVI_mean']))} </div>
        <div> std: {ndviF(get(historical, [y, 'stats', 'NDVI_stdDev']))} </div>
        <div> min: {ndviF(get(historical, [y, 'stats', 'NDVI_min']))} </div>
        <div> max: {ndviF(get(historical, [y, 'stats', 'NDVI_max']))} </div>
      </div>
    }))}/>
  </div>
}


const updateLayer = (mapboxRef, mapLayer, toggle) => {
  if (!mapboxRef.current) return;

  if (toggle) {
    mapLayer.forEach(l => {
      if (mapboxRef.current.getLayer(l.id)) mapboxRef.current.removeLayer(l.id);
      if (mapboxRef.current.getSource(l.id)) mapboxRef.current.removeSource(l.id);
      mapboxRef.current.addLayer(l);
    });
   
  } else {
    // TODO hack because useNdviToggle below doesn't keep mapLyaer fresh
    const id = 'ndvi';
    if (mapboxRef.current.getLayer(id)) mapboxRef.current.removeLayer(id);
    if (mapboxRef.current.getSource(id)) mapboxRef.current.removeSource(id);
  }

}


export const useNdviToggle = () => {
  const { mapboxRef, notify } = useContext(AppContext);
  const { boundary } = useContext(FarmContext);
  const { mapLayer } = useDates();

  const ndviToggle = (toggle) => {
    if (toggle && boundary.features.length === 0 ) {
      notify('No Boundary', 'Right click a parcel or draw one in the Boundary view', warningIcon);
    }
    updateLayer(mapboxRef, mapLayer, toggle);
  };

  return ndviToggle;
}


export default Map;

